import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import { ref, onValue, update } from 'firebase/database';
import { database } from '../firebase';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import { format } from 'date-fns';

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    olympiad: 'all',
    dateRange: 'all',
  });
  const theme = useTheme();

  useEffect(() => {
    const submissionsRef = ref(database, 'demoClasses');
    const unsubscribe = onValue(submissionsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const submissionsArray = Object.entries(data).map(([id, submission]) => ({
          id,
          ...submission,
        }));
        setSubmissions(submissionsArray);
        setFilteredSubmissions(submissionsArray);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...submissions];

    if (filters.status !== 'all') {
      filtered = filtered.filter((sub) => sub.status === filters.status);
    }

    if (filters.olympiad !== 'all') {
      filtered = filtered.filter((sub) => sub.olympiad === filters.olympiad);
    }

    if (filters.dateRange !== 'all') {
      const today = new Date();
      const submissionDate = new Date();
      
      switch (filters.dateRange) {
        case 'today':
          filtered = filtered.filter((sub) => {
            submissionDate.setTime(new Date(sub.timestamp).getTime());
            return submissionDate.toDateString() === today.toDateString();
          });
          break;
        case 'week':
          const weekAgo = new Date(today.setDate(today.getDate() - 7));
          filtered = filtered.filter((sub) => {
            submissionDate.setTime(new Date(sub.timestamp).getTime());
            return submissionDate >= weekAgo;
          });
          break;
        case 'month':
          const monthAgo = new Date(today.setMonth(today.getMonth() - 1));
          filtered = filtered.filter((sub) => {
            submissionDate.setTime(new Date(sub.timestamp).getTime());
            return submissionDate >= monthAgo;
          });
          break;
        default:
          break;
      }
    }

    setFilteredSubmissions(filtered);
  }, [filters, submissions]);

  const handleStatusChange = (submissionId, newStatus) => {
    const submissionRef = ref(database, `demoClasses/${submissionId}`);
    update(submissionRef, {
      status: newStatus,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleDelete = (submissionId) => {
    if (window.confirm('Are you sure you want to delete this submission?')) {
      const submissionRef = ref(database, `demoClasses/${submissionId}`);
      update(submissionRef, null);
    }
  };

  const handleOpenDialog = (submission) => {
    setSelectedSubmission(submission);
    setStatus(submission.status);
    setNotes(submission.notes || '');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSubmission(null);
    setStatus('');
    setNotes('');
  };

  const handleSaveNotes = () => {
    if (selectedSubmission) {
      const submissionRef = ref(database, `demoClasses/${selectedSubmission.id}`);
      update(submissionRef, {
        status,
        notes,
        updatedAt: new Date().toISOString(),
      });
      handleCloseDialog();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Demo Class Submissions
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Olympiad</InputLabel>
            <Select
              value={filters.olympiad}
              onChange={(e) => setFilters({ ...filters, olympiad: e.target.value })}
              label="Olympiad"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="International Mathematics Olympiad (IMO)">IMO</MenuItem>
              <MenuItem value="International Science Olympiad (ISO)">ISO</MenuItem>
              <MenuItem value="International English Olympiad (IEO)">IEO</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              label="Date Range"
            >
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Olympiad</TableCell>
              <TableCell>Class</TableCell>
              <TableCell>Preferred Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSubmissions.map((submission) => (
              <TableRow key={submission.id}>
                <TableCell>{submission.name}</TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">{submission.email}</Typography>
                    <Typography variant="body2">{submission.phone}</Typography>
                  </Box>
                </TableCell>
                <TableCell>{submission.olympiad}</TableCell>
                <TableCell>{submission.class}</TableCell>
                <TableCell>
                  {format(new Date(submission.preferredDate), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={submission.status}
                    color={getStatusColor(submission.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(submission)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(submission.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    color="info"
                    onClick={() => window.location.href = `mailto:${submission.email}`}
                  >
                    <EmailIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Edit Submission</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveNotes} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard; 